import React, { useRef, useState } from 'react';
import { Badge, Button, Form, Spinner } from 'react-bootstrap';
import { ArrowLeft, Plus, RotateCcw, Star, Upload, X } from 'lucide-react';
import { ALLOWED_PRODUCT_IMAGE_CONTENT_TYPES, ProductMediaClient } from '@/infrastructure/ProductMediaClient';

interface VariationImagesEditorProps {
  sku: string;
  /** The list currently shown: the variation's own images, or the parent's when inherited. */
  imageUrls: string[];
  /** `false` = inherited from the parent, `true` = the variation's own, `undefined` = unknown. */
  hasOwnImages: boolean | undefined;
  /** Called with the full new list. The first image is the thumbnail. */
  onChange: (imageUrls: string[]) => void;
  /** Drops the variation's own images so it inherits the parent's again. */
  onReset: () => void;
  /**
   * Called with `true` when an upload starts and `false` when it settles, so the page can hold Save until the
   * uploaded URL is in the list. Otherwise a save sent mid-upload leaves the image out.
   */
  onUploadingChange?: (isUploading: boolean) => void;
  client?: ProductMediaClient;
}

const THUMB_SIZE = 56;

/**
 * Edits a variation's own images (SSD-7417). Changes stay local until the editor's Save, which sends
 * the whole list as `variations[].images` - the featured-thumbnail endpoint rejects variation SKUs.
 * Editing an inherited list gives the variation its own copy; "Reset to parent" drops it again.
 */
export const VariationImagesEditor: React.FC<VariationImagesEditorProps> = ({
  sku,
  imageUrls,
  hasOwnImages,
  onChange,
  onReset,
  onUploadingChange,
  client,
}) => {
  const [newImageUrl, setNewImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mediaClient = client ?? new ProductMediaClient();
  const isInherited = hasOwnImages === false;
  const trimmedNewImageUrl = newImageUrl.trim();

  const moveToIndex = (from: number, to: number) => {
    const next = [...imageUrls];
    const [moved] = next.splice(from, 1);
    if (moved === undefined) {
      return;
    }
    next.splice(to, 0, moved);
    onChange(next);
  };

  const remove = (index: number) => onChange(imageUrls.filter((_, imageIndex) => imageIndex !== index));

  const addUrl = () => {
    if (!trimmedNewImageUrl) {
      return;
    }
    onChange([...imageUrls, trimmedNewImageUrl]);
    setNewImageUrl('');
  };

  const uploadFile = async (file: File | null) => {
    if (!file) {
      return;
    }
    setUploadError(null);
    setIsUploading(true);
    onUploadingChange?.(true);
    try {
      const uploadedUrl = await mediaClient.uploadImage(file);
      onChange([...imageUrls, uploadedUrl]);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to upload image');
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="col-md-12">
      <div className="d-flex align-items-center gap-2" style={{ marginTop: '0.75rem', marginBottom: '0.5rem' }}>
        <Form.Label className="mb-0" style={{ fontSize: '0.75rem', fontWeight: '600' }}>
          Variation images
        </Form.Label>
        {isInherited && (
          <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>
            Inherited from parent
          </Badge>
        )}
        {hasOwnImages === true && (
          <Badge bg="info" style={{ fontSize: '0.65rem' }}>
            Own images
          </Badge>
        )}
        {hasOwnImages === true && (
          <Button
            size="sm"
            variant="link"
            className="p-0 ms-auto"
            style={{ fontSize: '0.7rem' }}
            disabled={isUploading}
            onClick={onReset}
          >
            <RotateCcw size={12} />
            <span className="ms-1">Reset to parent</span>
          </Button>
        )}
      </div>

      {isInherited && (
        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>
          Showing the parent&apos;s images. Any edit gives this variation its own images on save.
        </div>
      )}

      <div className="d-flex flex-wrap gap-2 mb-2">
        {imageUrls.length === 0 && (
          <span className="text-muted" style={{ fontSize: '0.7rem' }}>
            No images
          </span>
        )}
        {imageUrls.map((url, index) => (
          <div
            key={`${url}-${index}`}
            className="position-relative"
            style={{
              width: THUMB_SIZE,
              opacity: isInherited ? 0.6 : 1,
            }}
          >
            <img
              src={url}
              alt={`Image ${index + 1} of ${sku}`}
              className="rounded"
              style={{
                width: THUMB_SIZE,
                height: THUMB_SIZE,
                objectFit: 'cover',
                border: index === 0 ? '2px solid #0d6efd' : '1px solid #dee2e6',
              }}
            />
            <div className="d-flex justify-content-between mt-1">
              {index === 0 ? (
                <span style={{ fontSize: '0.6rem', color: '#0d6efd', fontWeight: 600 }}>Thumbnail</span>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0"
                    aria-label={`Set image ${index + 1} of ${sku} as thumbnail`}
                    title="Set as thumbnail"
                    disabled={isUploading}
                    onClick={() => moveToIndex(index, 0)}
                  >
                    <Star size={12} />
                  </Button>
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0"
                    aria-label={`Move image ${index + 1} of ${sku} earlier`}
                    title="Move earlier"
                    disabled={isUploading}
                    onClick={() => moveToIndex(index, index - 1)}
                  >
                    <ArrowLeft size={12} />
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="link"
                className="p-0 text-danger"
                aria-label={`Remove image ${index + 1} of ${sku}`}
                title="Remove"
                disabled={isUploading}
                onClick={() => remove(index)}
              >
                <X size={12} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex gap-2 align-items-center">
        <Form.Control
          size="sm"
          type="url"
          placeholder="Paste an image URL to add"
          aria-label={`Image URL to add for ${sku}`}
          value={newImageUrl}
          disabled={isUploading}
          onChange={(event) => setNewImageUrl(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addUrl();
            }
          }}
        />
        <Button size="sm" variant="outline-primary" disabled={!trimmedNewImageUrl || isUploading} onClick={addUrl}>
          <Plus size={14} />
          <span className="ms-1">Add</span>
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading ? (
            <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
          ) : (
            <Upload size={14} />
          )}
          <span className="ms-1">{isUploading ? 'Uploading…' : 'Upload'}</span>
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="d-none"
        aria-label={`Image file to add for ${sku}`}
        accept={ALLOWED_PRODUCT_IMAGE_CONTENT_TYPES.join(',')}
        onChange={(event) => uploadFile(event.target.files?.[0] ?? null)}
      />

      {uploadError && (
        <div className="text-danger mt-1" style={{ fontSize: '0.7rem' }} role="alert">
          {uploadError}
        </div>
      )}
    </div>
  );
};
